import { useState } from 'react'
import { motion } from 'framer-motion'
import { MessageCircle, Send, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

const WHATSAPP_NUMBER = import.meta.env.VITE_WHATSAPP_NUMBER ?? '18092468383'
const WHATSAPP_MESSAGE = 'Hola, quisiera más información.'

function buildWhatsAppUrl(phone: string, text: string) {
  const clean = phone.replace(/\D/g, '')
  const encoded = encodeURIComponent(text)
  return `https://wa.me/${clean}?text=${encoded}`
}

export function SupportButton() {
  const [sheetOpen, setSheetOpen] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [sent, setSent] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Sin backend: solo feedback visual. Aquí podrías enviar a un API o mailto.
    setSent(true)
    setName('')
    setEmail('')
    setMessage('')
  }

  const openWhatsApp = () => {
    window.open(buildWhatsAppUrl(WHATSAPP_NUMBER, WHATSAPP_MESSAGE), '_blank', 'noopener,noreferrer')
  }

  return (
    <>
      <div className="fixed bottom-5 right-5 z-[79] flex flex-col items-end">
        <motion.button
          type="button"
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.96 }}
          onClick={() => setSheetOpen(true)}
          className="group relative flex items-center gap-2 rounded-full border border-[#E10F18]/30 bg-[#110E25] px-4 py-3 text-white shadow-[0_20px_55px_rgba(225,15,24,0.28)] transition-all hover:bg-[#1B1740]"
        >
          <span className="pointer-events-none absolute -inset-1 rounded-full bg-linear-to-r from-[#E10F18]/30 to-[#7C2D12]/20 blur-md" />
          <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-[#E10F18] shadow-inner">
            <MessageCircle className="h-5 w-5" />
          </div>
          <div className="relative hidden text-left sm:block">
            <p className="text-[11px] uppercase tracking-[0.12em] text-white/70">Soporte</p>
            <p className="text-sm font-semibold leading-tight">Contacto y sugerencias</p>
          </div>
        </motion.button>
      </div>

      <Sheet
        open={sheetOpen}
        onOpenChange={(open) => {
          setSheetOpen(open)
          if (!open) setSent(false)
        }}
      >
        <SheetContent side="right" className="w-full max-w-md overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle className="text-left">Soporte y contacto</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-8">
            <div>
              <p className="mb-3 text-sm text-muted-foreground">
                Habla con nosotros por WhatsApp o envíanos una sugerencia o consulta.
              </p>
              <Button
                type="button"
                onClick={openWhatsApp}
                className="w-full gap-2 bg-[#25D366] text-white hover:bg-[#20BD5A]"
              >
                <MessageCircle className="h-5 w-5" />
                Hablar por WhatsApp
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>

            <div className="border-t pt-6">
              <h3 className="mb-3 text-sm font-semibold">Buzón de sugerencias / Contacto</h3>
              {sent ? (
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
                  Gracias por tu mensaje. Te contactaremos pronto.
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="support-name">Nombre</Label>
                    <Input
                      id="support-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Tu nombre"
                      className="mt-1.5"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="support-email">Correo</Label>
                    <Input
                      id="support-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@correo.com"
                      className="mt-1.5"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="support-message">Mensaje, sugerencia o consulta</Label>
                    <Textarea
                      id="support-message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Escribe aquí..."
                      className="mt-1.5 min-h-[120px] resize-y"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full gap-2 bg-[#E10F18] hover:bg-[#C70D16]">
                    <Send className="h-4 w-4" />
                    Enviar
                  </Button>
                </form>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
